import { __awaiter } from "tslib";
/* eslint-disable no-param-reassign */
import 'intersection-observer';
import isEqual from 'lodash-es/isEqual';
import cookie from '@tencent/vft-common/cookie';
import { getUid, getVuid } from '@tencent/vft-common/login/index.esm';
import { loginType } from '@tencent/vft-common/login/lib';
import { getAIDPlatform as getPlatform } from '@tencent/vft-common/platform';
import storage from '@tencent/vft-common/Storage/session';
import { getUrlParam } from '@tencent/vft-common/util.esm';
import { BindingTypes, EventType, ReportElementType } from "../../defs";
import { getPvid, isPromisable, mapDt2BossParams, normalizePath } from "../../util";
/**
 * 预置上报参数
 * @type { ReportParams }
 */
export const presetParams = {
    /** url链接 */
    f_url: normalizePath(location.pathname),
    /** 域名（H5上报） */
    f_dm: location.host,
    /** 域名协议 */
    f_protocol: location.protocol,
    /** url参数，如果含有$，需要用|||替换，再上报 */
    f_url_param: location.search.split('?')[1] || '',
    /** ptag，优先取url中ptag参数，如果没有则取aid的13级； */
    f_pagetag: getUrlParam('ptag', location.href) || storage.get('ptag') || cookie.get('ptag'),
    f_pt: getPlatform(),
    hc_pvid: getPvid(),
};
export default class BossReporter {
    constructor(options, reportClient) {
        var _a;
        this.queue = [];
        this.reportClient = reportClient;
        this.presetParams = Object.assign({}, presetParams);
        this.init(options);
        this.observer = this.createObserver(options.observerOptions);
        this.enableRealtimePV = (_a = options.enableRealtimePV) !== null && _a !== void 0 ? _a : true;
        if (this.enableRealtimePV) {
            this.reportRealtimePV();
        }
    }
    init({ extraParams, delay } = {}) {
        extraParams && (this.extraParams = extraParams);
        this.delay = delay !== null && delay !== void 0 ? delay : this.delay;
    }
    ready() {
        this.delay = false;
        this.loginParams = this.buildLoginParams();
        this.pv();
        // 如果之前没有上报实时pv，在ready的时候上报一条冗余数据
        if (!this.enableRealtimePV) {
            this.reportRealtimePV();
        }
        let queueItem;
        while (queueItem = this.queue.shift()) {
            const [params, event] = queueItem;
            this.report(params, event);
        }
        return Promise.resolve();
    }
    report(params, event = EventType.EXPOSE, isRealtime = false) {
        return __awaiter(this, void 0, void 0, function* () {
            if (!params) {
                throw new Error('[Boss Report] report params is required');
            }
            if (this.delay && !isRealtime) {
                this.queue.push([params, event]);
                return;
            }
            const reportParams = yield this.buildReportParams(params, event);
            this.reportClient.report(reportParams, event);
        });
    }
    setExtraParams(params) {
        this.presetParams = Object.assign(Object.assign({}, this.presetParams), params);
    }
    useDirective(el, binding) {
        var _a;
        const { type, hook, once, module, value } = binding;
        if (!value) {
            console.warn(`[v-report:${hook}] 上报参数为空`, el);
        }
        if (hook === 'unbind' || !value) {
            if ((_a = el.report) === null || _a === void 0 ? void 0 : _a.click) {
                el.removeEventListener('click', el.report.click, true);
                el.report.click = null;
            }
            if (el.observed) {
                this.observer.unobserve(el);
            }
            return;
        }
        const params = value;
        const isModule = type === BindingTypes.MODULE;
        if (!isModule && module) {
            params.f_module = module;
        }
        if (el.report && isEqual(el.report.params, params)) {
            return;
        }
        if (!el.report) {
            el.report = {
                type,
                params,
            };
        }
        if (isModule) {
            el.report.params.f_type = ReportElementType.MODULE;
        }
        else {
            el.report.params = Object.assign(Object.assign({}, el.report.params), params);
        }
        if (once) {
            return;
        }
        // 注册 Dom 曝光上报
        const exposeTypes = [BindingTypes.MODULE, BindingTypes.ALL, BindingTypes.EXPOSE];
        if (exposeTypes.includes(type)) {
            if (el.observed) {
                this.observer.unobserve(el);
            }
            el.observed = true;
            this.observer.observe(el);
        }
        const clickTypes = [BindingTypes.ALL, BindingTypes.CLICK];
        // 首次注册时，未绑定点击事件
        if (clickTypes.includes(type) && !el.report.click) {
            el.report.click = () => {
                this.report(el.report.params, EventType.CLICK);
            };
            el.addEventListener('click', el.report.click, true);
        }
    }
    pv() {
        this.report({ f_type: ReportElementType.PAGE }, EventType.EXPOSE);
    }
    reportRealtimePV() {
        this.report({ f_type: ReportElementType.REALTIME_PAGE }, EventType.EXPOSE, true);
    }
    createObserver(observerOptions) {
        return new IntersectionObserver((entries) => {
            entries.forEach((entry) => {
                const { target, isIntersecting } = entry;
                if (isIntersecting) {
                    this.report(target.report.params, EventType.EXPOSE);
                }
            });
        }, Object.assign({ threshold: 0.1 }, observerOptions));
    }
    buildReportParams(params, event) {
        return __awaiter(this, void 0, void 0, function* () {
            let { presetParams } = this;
            if (typeof this.extraParams === 'function') {
                try {
                    let extraParams = this.extraParams();
                    if (isPromisable(extraParams)) {
                        extraParams = yield extraParams;
                    }
                    presetParams = Object.assign(Object.assign({}, this.presetParams), extraParams);
                }
                catch (e) {
                    console.error('[boss-report.buildReportParams] merge extraParams error', e);
                }
            }
            return Object.assign(Object.assign(Object.assign(Object.assign({}, this.loginParams), presetParams), mapDt2BossParams(params)), { f_event: event, _dc: Math.random() });
        });
    }
    buildLoginParams() {
        const currentLoginType = loginType();
        return {
            f_logintype: currentLoginType || '-',
            f_uid: getUid(),
            f_vuid: getVuid(),
            f_appid: cookie.get(`${currentLoginType === 'qq' ? 'vqq_' : ''}appid`),
        };
    }
}
